/**
 * Lite Storefront tab configuration — split out from the Modern theme.
 *
 * The Lite menu owns its own tab bar config (label, icon, enabled, order,
 * default) so admins can tune the Lite storefront without touching the
 * Modern theme — and vice-versa. Persisted to `khays_lite_tabs` in
 * localStorage and synced via cloudSync.
 *
 * Visitor's last active tab persists for the browser session only.
 */
import { create } from "zustand";

const KEY = "khays_lite_tabs";
const SESSION_KEY = "khays_lite_active_tab";

export type LiteTabId =
  | "home" | "available" | "offers" | "search" | "track" | "swap" | "settings"
  | "favorites" | "cart" | "profile";

export type LiteIconName =
  | "Home" | "Store" | "Tag" | "Search" | "Truck" | "ArrowLeftRight" | "Settings"
  | "ShoppingBag" | "Package" | "Sparkles" | "Compass" | "User"
  | "Heart" | "ShoppingCart";

export interface LiteTab {
  id: LiteTabId;
  label: string;
  icon: LiteIconName;
  enabled: boolean;
  order: number;
  customIconUrl?: string;
}

export interface LiteTabsConfig {
  tabs: LiteTab[];
  defaultTabId: LiteTabId;
}

const DEFAULTS: LiteTabsConfig = {
  defaultTabId: "home",
  tabs: [
    { id: "home",      label: "Home",      icon: "Home",            enabled: true,  order: 0 },
    { id: "available", label: "Available", icon: "Store",           enabled: true,  order: 1 },
    { id: "offers",    label: "Offers",    icon: "Tag",             enabled: true,  order: 2 },
    { id: "search",    label: "Search",    icon: "Search",          enabled: false, order: 3 },
    { id: "favorites", label: "Favorites", icon: "Heart",           enabled: true,  order: 4 },
    { id: "cart",      label: "Cart",      icon: "ShoppingCart",    enabled: true,  order: 5 },
    { id: "track",     label: "Track",     icon: "Truck",           enabled: true,  order: 6 },
    { id: "swap",      label: "Swap",      icon: "ArrowLeftRight",  enabled: true,  order: 7 },
    { id: "profile",   label: "Profile",   icon: "User",            enabled: false, order: 8 },
    { id: "settings",  label: "Settings",  icon: "Settings",        enabled: true,  order: 9 },
  ],
};

function load(): LiteTabsConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const p = JSON.parse(raw);
      const byId = new Map<LiteTabId, LiteTab>();
      for (const t of (p.tabs || [])) byId.set(t.id, t);
      const merged = DEFAULTS.tabs.map(d => byId.get(d.id) ? { ...d, ...byId.get(d.id)! } : d);
      return {
        tabs: merged.sort((a, b) => a.order - b.order),
        defaultTabId: p.defaultTabId || DEFAULTS.defaultTabId,
      };
    }
  } catch {}
  return DEFAULTS;
}

function persist(cfg: LiteTabsConfig) {
  try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch {}
}

interface State extends LiteTabsConfig {
  _rev: number;
  activeTab: LiteTabId;
  setActiveTab: (id: LiteTabId) => void;
  patchTab: (id: LiteTabId, p: Partial<LiteTab>) => void;
  setDefaultTabId: (id: LiteTabId) => void;
  setTabsOrder: (ids: LiteTabId[]) => void;
  reset: () => void;
  reload: () => void;
}

function readSessionActive(fallback: LiteTabId): LiteTabId {
  try {
    const v = sessionStorage.getItem(SESSION_KEY) as LiteTabId | null;
    if (v) return v;
  } catch {}
  return fallback;
}

export const useLiteTabs = create<State>((set, get) => {
  const initial = load();
  return {
    ...initial,
    _rev: 0,
    activeTab: readSessionActive(initial.defaultTabId),
    setActiveTab: (id) => {
      try { sessionStorage.setItem(SESSION_KEY, id); } catch {}
      set({ activeTab: id });
    },
    patchTab: (id, p) => {
      const next = get().tabs.map(t => t.id === id ? { ...t, ...p } : t);
      const cfg: LiteTabsConfig = { tabs: next, defaultTabId: get().defaultTabId };
      persist(cfg);
      set({ tabs: next, _rev: get()._rev + 1 });
    },
    setDefaultTabId: (id) => {
      const cfg: LiteTabsConfig = { tabs: get().tabs, defaultTabId: id };
      persist(cfg);
      set({ defaultTabId: id, _rev: get()._rev + 1 });
    },
    setTabsOrder: (ids) => {
      const map = new Map(get().tabs.map(t => [t.id, t]));
      const next = ids.map((id, i) => ({ ...(map.get(id)!), order: i })).filter(Boolean);
      const cfg: LiteTabsConfig = { tabs: next, defaultTabId: get().defaultTabId };
      persist(cfg);
      set({ tabs: next, _rev: get()._rev + 1 });
    },
    reset: () => {
      try { localStorage.removeItem(KEY); } catch {}
      set({ ...DEFAULTS, _rev: get()._rev + 1, activeTab: readSessionActive(DEFAULTS.defaultTabId) });
    },
    reload: () => {
      const fresh = load();
      set({ ...fresh, _rev: get()._rev + 1 });
    },
  };
});
